import React, {useMemo, useState} from "react";
import {ChevronRight, ChevronDown, ChevronUp} from "lucide-react";
import CreditsIconBlack from "../../assets/icons/credits.svg";
import creditActionEnum from "../../data/creditActionEnum";
import {useNavigate} from "react-router-dom";
import PropTypes from "prop-types";
import Button from "../Button/Button";

const CreditsDashboard = ({credits, showMax = null}) => {
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);

  const {totalCredits, perAction, recent} = useMemo(() => {
    const per = new Map();
    let total = 0;
    const sorted = [...credits].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    for (const credit of credits) {
      total += credit.credits || 0;
      const stat = per.get(credit.action) || {count: 0, sum: 0, lastAt: null};
      stat.count += 1;
      stat.sum += credit.credits || 0;
      if (!stat.lastAt || new Date(credit.created_at) > new Date(stat.lastAt)) {
        stat.lastAt = credit.created_at;
      }
      per.set(credit.action, stat);
    }

    return {
      totalCredits: total,
      perAction: per,
      recent: sorted,
    };
  }, [credits]);

  const formatDate = (iso) =>
    new Intl.DateTimeFormat('nl-NL', {
      day: "2-digit", month: "short"
    }).format(new Date(iso));

  // Filter alleen de "verdien" acties (positieve credits)
  const earnActions = creditActionEnum.filter(a => a.credits > 0);

  return (
    <div className="credits-dashboard-component">
      {/* Header met saldo en uitleg */}
      <div className="credits-intro">
        <div className="credits-balance">
          <img src={CreditsIconBlack} alt="Credits"/>
          <div className="balance-info">
            <span className="balance-label">Jouw Stand Credits</span>
            <span className="balance-value">{totalCredits}</span>
          </div>
        </div>

        <div className="credits-explainer">
          <h4>Wat zijn Stand Credits?</h4>
          <p>Credits zijn punten die je verdient door actief te zijn op Standplaats.nl. Je kunt ze gebruiken voor:</p>
          <ul>
            <li><strong>Korting op boekingen</strong> - bespaar op transactiekosten</li>
            <li><strong>Profiel boost</strong> - maak je profiel zichtbaarder en ontvang meer aanvragen</li>
          </ul>
        </div>
      </div>

      {/* Manieren om credits te verdienen */}
      <div className="credits-earn">
        <h4>Credits verdienen</h4>
        <div className="earn-list">
          {earnActions
            .slice(0, showMax || earnActions.length)
            .map((action) => {
              const stat = perAction.get(action.value) || {count: 0};
              const isDone = stat.count > 0;

              const hasAction = action.onClick !== null;

              return (
                <div
                  key={action.value}
                  className={`earn-item ${isDone ? 'done' : ''} ${hasAction ? 'clickable' : ''}`}
                  onClick={() => {
                    if (typeof action.onClick === 'string') {
                      navigate(action.onClick);
                    } else if (typeof action.onClick === 'function') {
                      action.onClick();
                    }
                  }}
                >
                  <div className="earn-icon">
                    <img src={action.icon} alt=""/>
                  </div>
                  <div className="earn-info">
                    <span className="earn-label">{action.label}</span>
                    {isDone && <span className="earn-done">{stat.count}× gedaan</span>}
                  </div>
                  <div className="earn-credits">
                    +{action.credits}
                  </div>
                  {hasAction && <ChevronRight className="earn-arrow"/>}
                </div>
              );
            })}
        </div>

        {showMax && earnActions.length > showMax && (
          <Button
            label="Bekijk alle manieren"
            color="grey-ghost"
            link="/user/credits"
          />
        )}
      </div>

      {/* Recente activiteit - inklapbaar */}
      {recent.length > 0 && (
        <div className="credits-history">
          <div
            className="history-toggle"
            onClick={() => setShowHistory(!showHistory)}
          >
            <span>Recente transacties ({recent.length})</span>
            {showHistory ? <ChevronUp/> : <ChevronDown/>}
          </div>

          {showHistory && (
            <ul className="history-list">
              {recent
                .slice(0, showMax ? 5 : recent.length)
                .map((credit) => {
                  const meta = creditActionEnum.find(a => a.value === credit.action);
                  const isNegative = credit.credits < 0;
                  return (
                    <li key={credit.id} className={isNegative ? 'spent' : 'earned'}>
                      <span className="history-date">{formatDate(credit.created_at)}</span>
                      <span className="history-label">{meta?.label || credit.action}</span>
                      <span className="history-credits">
                        {isNegative ? '' : '+'}{credit.credits}
                      </span>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

CreditsDashboard.propTypes = {
  credits: PropTypes.array.isRequired,
  showMax: PropTypes.number,
}

export default CreditsDashboard
